import {
  Check, AlertTriangle, Clock, Activity
} from 'lucide-react';

const RED = '#E02D45';
const GREEN = '#34D399';
const AMBER = '#FBBF24';
const BLUE_ACCENT = '#60A5FA';

/* Shared window chrome */
function MockupShell({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#0D0D0D] overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-white/[0.06] bg-white/[0.02]">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
        </div>
        <span className="text-[11px] text-white/30 font-mono ml-2">{title}</span>
      </div>
      {children}
    </div>
  );
}

function StatBox({ label, value, sub }) {
  return (
    <div className="px-4 py-3">
      <p className="text-[10px] text-white/25 font-medium uppercase tracking-wider">{label}</p>
      <p className="text-lg font-black text-white/80 mt-0.5">{value}</p>
      {sub && <p className="text-[10px] text-white/20 mt-0.5">{sub}</p>}
    </div>
  );
}

function StatusPill({ status, color }) {
  return (
    <span className="text-[11px] font-bold px-2 py-0.5 rounded-md" style={{ color, backgroundColor: `${color}15` }}>
      {status}
    </span>
  );
}

/* ═══════════════════════════════════════════
   1. INCOMING QUALITY | Inspection Dashboard
   ═══════════════════════════════════════════ */
export function IncomingQualityMockup() {
  return (
    <MockupShell title="Incoming Inspection Dashboard">
      <div className="grid grid-cols-3 border-b border-white/[0.06]">
        <div className="px-4 py-3 border-r border-white/[0.06]">
          <p className="text-[10px] text-white/25 uppercase tracking-wider">Inspected</p>
          <p className="text-2xl font-black text-white/80">47</p>
        </div>
        <div className="px-4 py-3 border-r border-white/[0.06]">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: GREEN }} />
            <p className="text-[10px] text-white/25 uppercase tracking-wider">Passed</p>
          </div>
          <p className="text-2xl font-black" style={{ color: GREEN }}>42</p>
        </div>
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: RED }} />
            <p className="text-[10px] text-white/25 uppercase tracking-wider">Rejected</p>
          </div>
          <p className="text-2xl font-black" style={{ color: RED }}>5</p>
        </div>
      </div>
      {/* GRN Table */}
      <div className="px-4 py-2">
        <div className="grid grid-cols-[1fr_auto_auto] gap-3 py-2 border-b border-white/[0.04] text-[10px] text-white/20 uppercase tracking-wider font-medium">
          <span>GRN No.</span><span>Supplier</span><span>Status</span>
        </div>
        {[
          { grn: 'GRN-1847', supplier: 'ABC Forge Ltd', status: 'Passed', color: GREEN },
          { grn: 'GRN-1848', supplier: 'PQ Castings', status: 'Pending', color: AMBER },
          { grn: 'GRN-1849', supplier: 'Rathi Metals', status: 'Rejected', color: RED },
          { grn: 'GRN-1850', supplier: 'Sharda Motor', status: 'Passed', color: GREEN },
          { grn: 'GRN-1851', supplier: 'SKH Metals', status: 'Passed', color: GREEN },
        ].map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-3 py-2.5 border-b border-white/[0.03] items-center">
            <span className="text-[12px] text-white/50 font-mono font-medium">{r.grn}</span>
            <span className="text-[11px] text-white/30">{r.supplier}</span>
            <StatusPill status={r.status} color={r.color} />
          </div>
        ))}
      </div>
      {/* Certificate Strip */}
      <div className="px-4 py-3 border-t border-white/[0.06] bg-white/[0.015] flex items-center justify-between">
        <span className="text-[10px] text-white/20 uppercase tracking-wider">Supplier Certs</span>
        <div className="flex gap-1.5">
          {['MTC', 'CoC', 'PDI', 'Test'].map(c => (
            <span key={c} className="text-[9px] font-bold px-2 py-0.5 rounded bg-white/[0.05] text-white/35">{c}</span>
          ))}
        </div>
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   2. DIGITAL STORES | Rack/Bin Matrix
   ═══════════════════════════════════════════ */
export function DigitalStoresMockup() {
  const bins = [
    [1,1,0.5,1,0,1], [1,0.5,1,1,1,0.5], [0,1,1,0.5,1,1],
    [1,1,1,0,0.5,1], [0.5,1,0,1,1,1],
  ];
  return (
    <MockupShell title="Store Inventory | Rack A through E">
      <div className="p-4">
        {/* Rack/Bin Grid */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] text-white/20 uppercase tracking-wider w-12">Rack</span>
          {[1,2,3,4,5,6].map(b => (
            <span key={b} className="text-[9px] text-white/15 font-mono flex-1 text-center">Bin {b}</span>
          ))}
        </div>
        {bins.map((row, ri) => (
          <div key={ri} className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] text-white/30 font-mono w-12">{String.fromCharCode(65 + ri)}</span>
            {row.map((v, ci) => (
              <div key={ci} className="flex-1 h-7 rounded-md" style={{
                backgroundColor: v === 1 ? `${GREEN}18` : v === 0.5 ? `${AMBER}18` : `${RED}12`,
                border: `1px solid ${v === 1 ? `${GREEN}30` : v === 0.5 ? `${AMBER}30` : `${RED}20`}`,
              }}>
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full" style={{
                    backgroundColor: v === 1 ? GREEN : v === 0.5 ? AMBER : RED,
                    opacity: 0.6
                  }} />
                </div>
              </div>
            ))}
          </div>
        ))}
        <div className="flex gap-4 mt-3 justify-end">
          {[{l:'In Stock',c:GREEN},{l:'Low',c:AMBER},{l:'Empty',c:RED}].map(x => (
            <div key={x.l} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: x.c, opacity: 0.5 }} />
              <span className="text-[9px] text-white/20">{x.l}</span>
            </div>
          ))}
        </div>
      </div>
      {/* FIFO Bar */}
      <div className="px-4 py-3 border-t border-white/[0.06] bg-white/[0.015]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-white/25 uppercase tracking-wider">FIFO Compliance</span>
          <span className="text-[12px] font-bold" style={{ color: GREEN }}>99.1%</span>
        </div>
        <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: '99.1%', backgroundColor: GREEN, opacity: 0.5 }} />
        </div>
      </div>
      <div className="grid grid-cols-3 border-t border-white/[0.06]">
        <StatBox label="Total SKUs" value="1,247" />
        <StatBox label="Active Bins" value="384" />
        <StatBox label="Lots Tracked" value="2,891" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   3. PRODUCTION EXECUTION | Output Chart
   ═══════════════════════════════════════════ */
export function ProductionMockup() {
  const hours = [
    { h: '08:00', actual: 85, target: 90 },
    { h: '09:00', actual: 92, target: 90 },
    { h: '10:00', actual: 88, target: 90 },
    { h: '11:00', actual: 76, target: 90 },
    { h: '12:00', actual: 91, target: 90 },
    { h: '13:00', actual: 94, target: 90 },
    { h: '14:00', actual: 70, target: 90 },
    { h: '15:00', actual: 89, target: 90 },
  ];
  return (
    <MockupShell title="Production Dashboard | Shift B">
      <div className="grid grid-cols-4 border-b border-white/[0.06]">
        <StatBox label="Output" value="2,847" />
        <StatBox label="Target" value="3,200" />
        <StatBox label="Rejection" value="1.2%" />
        <StatBox label="OEE" value="82.4%" />
      </div>
      {/* Hourly bar chart */}
      <div className="p-4">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Hourly Output vs Target</p>
        <div className="flex items-end gap-2 h-24">
          {hours.map((h, i) => {
            const pct = (h.actual / 100) * 100;
            const tPct = (h.target / 100) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full relative" style={{ height: '80px' }}>
                  {/* target line */}
                  <div className="absolute w-full border-t border-dashed" style={{ bottom: `${tPct * 0.8}%`, borderColor: `${AMBER}40` }} />
                  {/* actual bar */}
                  <div className="absolute bottom-0 w-full rounded-t-sm" style={{
                    height: `${pct * 0.8}%`,
                    backgroundColor: h.actual >= h.target ? `${GREEN}40` : `${RED}30`,
                    border: `1px solid ${h.actual >= h.target ? `${GREEN}50` : `${RED}40`}`,
                    borderBottom: 'none',
                  }} />
                </div>
                <span className="text-[8px] text-white/15 font-mono">{h.h.split(':')[0]}</span>
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-2 justify-end">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: `${GREEN}50` }} />
            <span className="text-[9px] text-white/20">On Target</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: `${RED}40` }} />
            <span className="text-[9px] text-white/20">Below Target</span>
          </div>
        </div>
      </div>
      {/* Line status */}
      <div className="border-t border-white/[0.06]">
        {[
          { line: 'Line 1 / CNC-04', st: 'Running', c: GREEN },
          { line: 'Line 2 / Press-07', st: 'Running', c: GREEN },
          { line: 'Line 3 / Weld-02', st: 'Idle', c: AMBER },
        ].map((l, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03]">
            <span className="text-[12px] text-white/40 font-mono">{l.line}</span>
            <StatusPill status={l.st} color={l.c} />
          </div>
        ))}
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   4. QUALITY MANAGEMENT | SPC + Checksheet
   ═══════════════════════════════════════════ */
export function QualityMockup() {
  // SPC data points
  const spcPoints = [52, 50, 51, 49, 53, 50, 48, 51, 54, 50, 49, 52, 51, 47, 50, 53, 51, 50, 49, 52];
  const ucl = 55, lcl = 45, mean = 50;
  const maxV = 58, minV = 42;
  const toY = (v) => ((maxV - v) / (maxV - minV)) * 60;
  return (
    <MockupShell title="Quality Control Panel">
      {/* SPC Chart */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] text-white/25 uppercase tracking-wider">SPC Run Chart | Bore Diameter</span>
          <span className="text-[10px] font-bold" style={{ color: GREEN }}>Cp: 1.67 / Cpk: 1.54</span>
        </div>
        <svg viewBox="0 0 200 70" className="w-full h-20">
          {/* UCL/LCL/Mean lines */}
          <line x1="0" y1={toY(ucl)} x2="200" y2={toY(ucl)} stroke={RED} strokeWidth="0.5" strokeDasharray="3,3" opacity="0.4" />
          <line x1="0" y1={toY(lcl)} x2="200" y2={toY(lcl)} stroke={RED} strokeWidth="0.5" strokeDasharray="3,3" opacity="0.4" />
          <line x1="0" y1={toY(mean)} x2="200" y2={toY(mean)} stroke="white" strokeWidth="0.3" opacity="0.15" />
          {/* Data line */}
          <polyline
            fill="none"
            stroke={GREEN}
            strokeWidth="1.2"
            opacity="0.7"
            points={spcPoints.map((v, i) => `${(i / (spcPoints.length - 1)) * 196 + 2},${toY(v)}`).join(' ')}
          />
          {/* Data dots */}
          {spcPoints.map((v, i) => (
            <circle key={i} cx={(i / (spcPoints.length - 1)) * 196 + 2} cy={toY(v)} r="1.5" fill={GREEN} opacity="0.5" />
          ))}
          <text x="2" y={toY(ucl) - 3} fill={RED} fontSize="5" opacity="0.5">UCL</text>
          <text x="2" y={toY(lcl) + 8} fill={RED} fontSize="5" opacity="0.5">LCL</text>
        </svg>
      </div>
      {/* Checksheet Grid */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">E-Checksheet | Setup Verification</p>
        <div className="grid grid-cols-4 gap-1.5">
          {['Torque', 'Dimension A', 'Surface', 'Runout', 'Hardness', 'Visual', 'Thread', 'Bore'].map((c, i) => (
            <div key={i} className="flex items-center gap-1.5 py-1.5 px-2 rounded bg-white/[0.03] border border-white/[0.04]">
              <Check className="w-3 h-3" style={{ color: i === 5 ? AMBER : GREEN }} strokeWidth={2.5} />
              <span className="text-[9px] text-white/35 font-medium">{c}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Quality summary */}
      <div className="grid grid-cols-3">
        <StatBox label="Checks Today" value="312" />
        <StatBox label="NCR Open" value="4" />
        <StatBox label="Red Bin Items" value="3" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   5. CHANGE MANAGEMENT | Approval Flow
   ═══════════════════════════════════════════ */
export function ChangeMgmtMockup() {
  const changes = [
    { type: 'Man', desc: 'Operator change L3', status: 'Approved', color: GREEN },
    { type: 'Machine', desc: 'Die swap CNC-04', status: 'In Review', color: AMBER },
    { type: 'Material', desc: 'Lot 2847-B alternate', status: 'Approved', color: GREEN },
    { type: 'Method', desc: 'Torque spec update', status: 'Pending', color: AMBER },
  ];
  return (
    <MockupShell title="4M Change Control Board">
      {/* Workflow Stages */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Approval Workflow</p>
        <div className="flex items-center gap-1">
          {['Initiation', 'Review', 'Evidence', 'QA Sign-off', 'Closure'].map((s, i) => (
            <div key={i} className="flex items-center gap-1 flex-1">
              <div className={`flex-1 h-7 rounded-md flex items-center justify-center text-[9px] font-bold ${i < 3 ? 'text-white/60' : 'text-white/20'}`}
                style={{
                  backgroundColor: i < 3 ? `${GREEN}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${i < 3 ? `${GREEN}30` : 'rgba(255,255,255,0.06)'}`,
                }}>
                {s}
              </div>
              {i < 4 && <div className="text-white/10 text-[10px]">&rsaquo;</div>}
            </div>
          ))}
        </div>
      </div>
      {/* 4M Change List */}
      <div>
        {changes.map((c, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-white/[0.04]">
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded" style={{ color: RED, backgroundColor: `${RED}12` }}>{c.type}</span>
              <span className="text-[12px] text-white/45 font-medium">{c.desc}</span>
            </div>
            <StatusPill status={c.status} color={c.color} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 border-t border-white/[0.06]">
        <StatBox label="Changes MTD" value="23" />
        <StatBox label="Violations" value="2" />
        <StatBox label="Closure Rate" value="91%" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   6. MAINTENANCE | PM Planner + MTTR/MTBF
   ═══════════════════════════════════════════ */
export function MaintenanceMockup() {
  return (
    <MockupShell title="Maintenance Dashboard">
      {/* PM Timeline */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">PM Schedule | This Week</p>
        {[
          { machine: 'CNC-04', days: [0,0,1,0,0], label: 'Wed' },
          { machine: 'Press-07', days: [1,0,0,0,0], label: 'Mon' },
          { machine: 'Weld-02', days: [0,0,0,1,0], label: 'Thu' },
          { machine: 'Assy-01', days: [0,0,0,0,1], label: 'Fri' },
        ].map((m, i) => (
          <div key={i} className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] text-white/30 font-mono w-16 truncate">{m.machine}</span>
            <div className="flex gap-1 flex-1">
              {['Mon','Tue','Wed','Thu','Fri'].map((d, di) => (
                <div key={di} className="flex-1 h-5 rounded-sm flex items-center justify-center" style={{
                  backgroundColor: m.days[di] ? `${RED}20` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${m.days[di] ? `${RED}35` : 'rgba(255,255,255,0.04)'}`,
                }}>
                  {m.days[di] ? <span className="text-[8px] font-bold" style={{ color: RED }}>PM</span> : null}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex gap-3 mt-1">
          {['Mon','Tue','Wed','Thu','Fri'].map(d => (
            <span key={d} className="flex-1 text-center text-[8px] text-white/12 ml-[72px] first:ml-[72px]">{d}</span>
          ))}
        </div>
      </div>
      {/* MTTR/MTBF bars */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">MTTR / MTBF Analysis</p>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-[10px] text-white/30">MTTR</span>
              <span className="text-[10px] font-bold" style={{ color: AMBER }}>42 min</span>
            </div>
            <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: '35%', backgroundColor: AMBER, opacity: 0.5 }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-[10px] text-white/30">MTBF</span>
              <span className="text-[10px] font-bold" style={{ color: GREEN }}>187 hrs</span>
            </div>
            <div className="w-full h-2 bg-white/[0.04] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: '78%', backgroundColor: GREEN, opacity: 0.5 }} />
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="PM Compliance" value="94%" />
        <StatBox label="Breakdowns" value="2" />
        <StatBox label="Spares OK" value="97%" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   7. CALIBRATION LAB | Calendar + Certs
   ═══════════════════════════════════════════ */
export function CalibrationMockup() {
  const days = Array.from({length: 28}, (_, i) => i + 1);
  const due = [5, 12, 18, 25];
  const overdue = [3];
  return (
    <MockupShell title="Calibration Schedule | January">
      {/* Calendar Grid */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="grid grid-cols-7 gap-1">
          {['S','M','T','W','T','F','S'].map((d,i) => (
            <span key={i} className="text-[8px] text-white/15 text-center py-1">{d}</span>
          ))}
          {/* offset for starting day */}
          <div /><div />
          {days.map(d => {
            const isDue = due.includes(d);
            const isOverdue = overdue.includes(d);
            return (
              <div key={d} className="aspect-square rounded-sm flex items-center justify-center text-[9px] font-mono" style={{
                backgroundColor: isOverdue ? `${RED}20` : isDue ? `${AMBER}15` : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isOverdue ? `${RED}30` : isDue ? `${AMBER}25` : 'rgba(255,255,255,0.03)'}`,
                color: isOverdue ? RED : isDue ? AMBER : 'rgba(255,255,255,0.2)',
              }}>
                {d}
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-2 justify-end">
          {[{l:'Due',c:AMBER},{l:'Overdue',c:RED}].map(x => (
            <div key={x.l} className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: x.c, opacity: 0.4 }} />
              <span className="text-[8px] text-white/20">{x.l}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Instrument list */}
      <div>
        {[
          { name: 'Vernier V-047', msa: 'Done', status: 'Calibrated', c: GREEN },
          { name: 'Micrometer M-012', msa: 'Done', status: 'Due Soon', c: AMBER },
          { name: 'Height Gauge HG-08', msa: 'Pending', status: 'Calibrated', c: GREEN },
          { name: 'Ring Gauge RG-23', msa: 'Done', status: 'Overdue', c: RED },
        ].map((inst, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03]">
            <div>
              <span className="text-[12px] text-white/45 font-mono">{inst.name}</span>
              <span className="text-[9px] text-white/15 ml-2">MSA: {inst.msa}</span>
            </div>
            <StatusPill status={inst.status} color={inst.c} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 border-t border-white/[0.06]">
        <StatBox label="Instruments" value="284" />
        <StatBox label="Calibrated" value="271" />
        <StatBox label="MSA Done" value="89%" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   8. COMPLAINTS & CAPA | Pareto + 8D Pipeline
   ═══════════════════════════════════════════ */
export function ComplaintsMockup() {
  const pareto = [
    { issue: 'Dimension', count: 12 },
    { issue: 'Surface', count: 8 },
    { issue: 'Packaging', count: 5 },
    { issue: 'Mixed Parts', count: 3 },
    { issue: 'Other', count: 2 },
  ];
  const maxCount = 14;
  return (
    <MockupShell title="Complaint Tracker | Pareto Analysis">
      {/* Pareto Chart */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex items-end gap-2 h-20">
          {pareto.map((p, i) => (
            <div key={i} className="flex-1 flex flex-col items-center">
              <span className="text-[9px] text-white/30 font-bold mb-1">{p.count}</span>
              <div className="w-full rounded-t-sm" style={{
                height: `${(p.count / maxCount) * 60}px`,
                backgroundColor: i === 0 ? `${RED}45` : `${RED}${25 - i * 5}`,
                border: `1px solid ${RED}40`,
                borderBottom: 'none',
              }} />
              <span className="text-[8px] text-white/20 mt-1 text-center leading-tight">{p.issue}</span>
            </div>
          ))}
        </div>
        {/* Cumulative line overlay */}
        <svg viewBox="0 0 200 10" className="w-full h-3 -mt-1">
          <polyline fill="none" stroke={AMBER} strokeWidth="0.8" opacity="0.6"
            points="20,8 60,5 100,3 140,2 180,1.5" />
        </svg>
      </div>
      {/* 8D Pipeline */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">8D CAPA Pipeline</p>
        <div className="flex gap-1">
          {['D1','D2','D3','D4','D5','D6','D7','D8'].map((d, i) => (
            <div key={d} className="flex-1 h-8 rounded-sm flex flex-col items-center justify-center" style={{
              backgroundColor: i < 5 ? `${GREEN}12` : i < 7 ? `${AMBER}12` : 'rgba(255,255,255,0.02)',
              border: `1px solid ${i < 5 ? `${GREEN}25` : i < 7 ? `${AMBER}25` : 'rgba(255,255,255,0.05)'}`,
            }}>
              <span className="text-[9px] font-bold" style={{ color: i < 5 ? GREEN : i < 7 ? AMBER : 'rgba(255,255,255,0.2)' }}>{d}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Open" value="7" />
        <StatBox label="Repeat" value="1" />
        <StatBox label="Avg Close" value="6 days" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   9. AUDIT MANAGEMENT | Scorecards + Findings
   ═══════════════════════════════════════════ */
export function AuditMockup() {
  const audits = [
    { type: 'Process', score: 92, findings: 3 },
    { type: 'Product', score: 95, findings: 1 },
    { type: 'Layered', score: 87, findings: 5 },
    { type: 'Dock', score: 78, findings: 3 },
  ];
  return (
    <MockupShell title="Audit Dashboard">
      {/* Donut Scorecards */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Audit Scores</p>
        <div className="grid grid-cols-4 gap-3">
          {audits.map((a, i) => {
            const color = a.score >= 90 ? GREEN : a.score >= 80 ? AMBER : RED;
            const circumference = 2 * Math.PI * 18;
            const offset = circumference * (1 - a.score / 100);
            return (
              <div key={i} className="flex flex-col items-center">
                <svg viewBox="0 0 44 44" className="w-12 h-12">
                  <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="3" />
                  <circle cx="22" cy="22" r="18" fill="none" stroke={color} strokeWidth="3" opacity="0.6"
                    strokeDasharray={circumference} strokeDashoffset={offset}
                    strokeLinecap="round" transform="rotate(-90 22 22)" />
                  <text x="22" y="24" textAnchor="middle" fill="white" fontSize="9" fontWeight="900" opacity="0.7">{a.score}%</text>
                </svg>
                <span className="text-[9px] text-white/30 mt-1">{a.type}</span>
              </div>
            );
          })}
        </div>
      </div>
      {/* Findings tracker */}
      <div>
        {[
          { finding: 'NC-041: Missing torque record', severity: 'Major', c: RED },
          { finding: 'NC-040: Gauge tag expired', severity: 'Minor', c: AMBER },
          { finding: 'NC-039: 5S non-compliance', severity: 'Minor', c: AMBER },
          { finding: 'NC-038: SPC chart not updated', severity: 'Obs', c: BLUE_ACCENT },
        ].map((f, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03]">
            <span className="text-[11px] text-white/40">{f.finding}</span>
            <StatusPill status={f.severity} color={f.c} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 border-t border-white/[0.06]">
        <StatBox label="Audits MTD" value="34" />
        <StatBox label="Open Findings" value="12" />
        <StatBox label="Avg Score" value="91%" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   10. TRAINING / DOJO | Skill Matrix + Donuts
   ═══════════════════════════════════════════ */
export function TrainingMockup() {
  const skills = [
    { name: 'R. Kumar', ops: [3,2,3,1,0] },
    { name: 'S. Patel', ops: [2,3,1,2,1] },
    { name: 'M. Singh', ops: [3,3,2,3,2] },
    { name: 'A. Das', ops: [1,0,2,1,3] },
  ];
  const ops = ['CNC', 'Press', 'Weld', 'Assy', 'Grind'];
  const levelColor = (l) => l === 3 ? GREEN : l === 2 ? AMBER : l === 1 ? `${RED}` : 'rgba(255,255,255,0.06)';
  return (
    <MockupShell title="Training Dashboard | Skill Matrix">
      {/* Matrix Grid */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-1 mb-2">
          <span className="text-[9px] text-white/15 w-16"></span>
          {ops.map(o => <span key={o} className="flex-1 text-center text-[8px] text-white/20 font-medium">{o}</span>)}
        </div>
        {skills.map((s, i) => (
          <div key={i} className="flex items-center gap-1 mb-1">
            <span className="text-[9px] text-white/30 w-16 truncate">{s.name}</span>
            {s.ops.map((l, j) => (
              <div key={j} className="flex-1 h-6 rounded-sm flex items-center justify-center" style={{
                backgroundColor: `${levelColor(l)}${l > 0 ? '18' : ''}`,
                border: `1px solid ${l > 0 ? `${levelColor(l)}35` : 'rgba(255,255,255,0.04)'}`,
              }}>
                {l > 0 && <span className="text-[8px] font-bold" style={{ color: levelColor(l), opacity: 0.7 }}>L{l}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
      {/* Skill Distribution Donuts */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Skill Distribution</p>
        <div className="flex justify-around">
          {[
            { label: 'L3', pct: 35, color: GREEN },
            { label: 'L2', pct: 40, color: AMBER },
            { label: 'L1', pct: 25, color: RED },
          ].map((d) => {
            const circ = 2 * Math.PI * 14;
            return (
              <div key={d.label} className="flex flex-col items-center">
                <svg viewBox="0 0 36 36" className="w-10 h-10">
                  <circle cx="18" cy="18" r="14" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="3" />
                  <circle cx="18" cy="18" r="14" fill="none" stroke={d.color} strokeWidth="3" opacity="0.6"
                    strokeDasharray={circ} strokeDashoffset={circ * (1 - d.pct / 100)}
                    strokeLinecap="round" transform="rotate(-90 18 18)" />
                  <text x="18" y="20" textAnchor="middle" fill={d.color} fontSize="8" fontWeight="900" opacity="0.8">{d.pct}%</text>
                </svg>
                <span className="text-[9px] text-white/25 mt-1">{d.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Trained MTD" value="127" />
        <StatBox label="Avg Score" value="84%" />
        <StatBox label="Coverage" value="91%" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   11. NPD / APQP | Timeline + Milestones
   ═══════════════════════════════════════════ */
export function NpdApqpMockup() {
  const phases = [
    { name: 'Planning', pct: 100, color: GREEN },
    { name: 'Product Design', pct: 100, color: GREEN },
    { name: 'Process Design', pct: 65, color: AMBER },
    { name: 'Validation', pct: 10, color: 'rgba(255,255,255,0.15)' },
    { name: 'Launch', pct: 0, color: 'rgba(255,255,255,0.08)' },
  ];
  return (
    <MockupShell title="APQP Project Tracker | Project Alpha">
      {/* Phase Timeline */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">APQP Phase Progress</p>
        <div className="space-y-2">
          {phases.map((p, i) => (
            <div key={i}>
              <div className="flex justify-between mb-1">
                <span className="text-[10px] text-white/35 font-medium">{p.name}</span>
                <span className="text-[10px] font-bold" style={{ color: p.pct === 100 ? GREEN : p.pct > 0 ? AMBER : 'rgba(255,255,255,0.15)' }}>{p.pct}%</span>
              </div>
              <div className="w-full h-2 bg-white/[0.03] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(p.pct, 2)}%`, backgroundColor: p.color, opacity: p.pct > 0 ? 0.5 : 0.1 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Milestone + PPAP Board */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">PPAP Release Board</p>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { doc: 'FMEA', st: 'Released', c: GREEN },
            { doc: 'Control Plan', st: 'Released', c: GREEN },
            { doc: 'MSA', st: 'In Progress', c: AMBER },
            { doc: 'SPC', st: 'Pending', c: 'rgba(255,255,255,0.2)' },
            { doc: 'Dim Report', st: 'Released', c: GREEN },
            { doc: 'Material Cert', st: 'Released', c: GREEN },
          ].map((d, i) => (
            <div key={i} className="p-2 rounded-md bg-white/[0.02] border border-white/[0.04]">
              <p className="text-[9px] text-white/30 font-medium">{d.doc}</p>
              <p className="text-[9px] font-bold mt-0.5" style={{ color: d.c }}>{d.st}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Active Projects" value="8" />
        <StatBox label="On-Time" value="75%" />
        <StatBox label="PPAP Pending" value="3" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   12. MANAGEMENT DASHBOARDS | Executive View
   ═══════════════════════════════════════════ */
export function ManagementMockup() {
  return (
    <MockupShell title="Executive Dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-4 border-b border-white/[0.06]">
        {[
          { label: 'Plant OEE', value: '84.2%', trend: '+2.1%', up: true },
          { label: 'Customer PPM', value: '126', trend: '-18', up: true },
          { label: 'Internal PPM', value: '842', trend: '+45', up: false },
          { label: 'OTIF', value: '97.8%', trend: '+0.3%', up: true },
        ].map((k, i) => (
          <div key={i} className={`px-4 py-3 ${i < 3 ? 'border-r border-white/[0.06]' : ''}`}>
            <p className="text-[10px] text-white/25 uppercase tracking-wider">{k.label}</p>
            <p className="text-xl font-black text-white/80 mt-0.5">{k.value}</p>
            <p className="text-[10px] font-bold mt-0.5" style={{ color: k.up ? GREEN : RED }}>{k.trend}</p>
          </div>
        ))}
      </div>
      {/* Trend Chart + Pie Chart side by side */}
      <div className="grid grid-cols-2 border-b border-white/[0.06]">
        {/* Rejection Trend Line */}
        <div className="p-4 border-r border-white/[0.06]">
          <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Rejection Trend</p>
          <svg viewBox="0 0 120 40" className="w-full h-12">
            <polyline fill="none" stroke={RED} strokeWidth="1.2" opacity="0.6"
              points="5,30 20,28 35,25 50,20 65,22 80,18 95,15 110,12" />
            <polyline fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" strokeDasharray="2,2"
              points="5,20 110,20" />
            {[5,20,35,50,65,80,95,110].map((x,i) => (
              <circle key={i} cx={x} cy={[30,28,25,20,22,18,15,12][i]} r="1.5" fill={RED} opacity="0.5" />
            ))}
          </svg>
        </div>
        {/* Department Health Pie */}
        <div className="p-4">
          <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Dept. Health</p>
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 44 44" className="w-14 h-14">
              <circle cx="22" cy="22" r="16" fill="none" stroke={GREEN} strokeWidth="4" opacity="0.5"
                strokeDasharray={`${0.6 * 100.5} ${100.5}`} transform="rotate(-90 22 22)" />
              <circle cx="22" cy="22" r="16" fill="none" stroke={AMBER} strokeWidth="4" opacity="0.5"
                strokeDasharray={`${0.25 * 100.5} ${100.5}`} strokeDashoffset={`${-0.6 * 100.5}`} transform="rotate(-90 22 22)" />
              <circle cx="22" cy="22" r="16" fill="none" stroke={RED} strokeWidth="4" opacity="0.5"
                strokeDasharray={`${0.15 * 100.5} ${100.5}`} strokeDashoffset={`${-0.85 * 100.5}`} transform="rotate(-90 22 22)" />
            </svg>
            <div className="space-y-1">
              {[{l:'Good',c:GREEN,v:'60%'},{l:'Fair',c:AMBER,v:'25%'},{l:'At Risk',c:RED,v:'15%'}].map(x=>(
                <div key={x.l} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{backgroundColor:x.c,opacity:0.5}} />
                  <span className="text-[9px] text-white/25">{x.l} {x.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Plants Active" value="3" />
        <StatBox label="Alerts Today" value="14" />
        <StatBox label="Reports Sent" value="8" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   13. TRACEABILITY | Chain Flow
   ═══════════════════════════════════════════ */
export function TraceabilityMockup() {
  const chain = ['Inward', 'IQC', 'Store', 'Issue', 'Production', 'In-Process QC', 'Final QC', 'Dispatch'];
  return (
    <MockupShell title="Traceability Chain | Part #28471">
      {/* Flow Chain */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-3">Digital Genealogy</p>
        <div className="flex flex-wrap gap-y-2">
          {chain.map((s, i) => (
            <div key={i} className="flex items-center">
              <div className="px-2.5 py-1.5 rounded-md text-[9px] font-bold" style={{
                backgroundColor: `${GREEN}12`,
                border: `1px solid ${GREEN}30`,
                color: GREEN,
              }}>{s}</div>
              {i < chain.length - 1 && (
                <div className="mx-1 text-white/15 text-[10px]">&rarr;</div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Linkage Details */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Linked Events</p>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            { label: 'Barcode', value: 'BC-28471-A4' },
            { label: 'Lot', value: 'LOT-2847' },
            { label: 'Machine', value: 'CNC-04' },
            { label: 'Operator', value: 'R. Kumar' },
            { label: 'IQC Result', value: 'Passed' },
            { label: 'FQC Result', value: 'Passed' },
          ].map((e, i) => (
            <div key={i} className="flex items-center justify-between px-2.5 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]">
              <span className="text-[9px] text-white/25">{e.label}</span>
              <span className="text-[9px] font-bold text-white/50">{e.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Touchpoints" value="12" />
        <StatBox label="Machine Events" value="4" />
        <StatBox label="QC Checks" value="6" />
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   14. IOT & MACHINE DATA | Live Dashboard
   ═══════════════════════════════════════════ */
export function IoTMockup() {
  return (
    <MockupShell title="Live Machine Dashboard">
      {/* OEE Gauge + Andon */}
      <div className="grid grid-cols-2 border-b border-white/[0.06]">
        <div className="p-4 border-r border-white/[0.06] flex flex-col items-center">
          <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2 self-start">OEE</p>
          <svg viewBox="0 0 60 36" className="w-20 h-12">
            {/* Gauge arc background */}
            <path d="M6,32 A24,24 0 0,1 54,32" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" strokeLinecap="round" />
            {/* Gauge arc value (82%) */}
            <path d="M6,32 A24,24 0 0,1 54,32" fill="none" stroke={GREEN} strokeWidth="4" strokeLinecap="round" opacity="0.6"
              strokeDasharray={`${0.82 * 75.4} 75.4`} />
            <text x="30" y="30" textAnchor="middle" fill="white" fontSize="11" fontWeight="900" opacity="0.7">82%</text>
          </svg>
        </div>
        {/* Andon Status */}
        <div className="p-4">
          <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Andon Status</p>
          <div className="flex gap-2">
            {[
              { label: 'Running', count: 14, c: GREEN },
              { label: 'Idle', count: 4, c: AMBER },
              { label: 'Down', count: 2, c: RED },
            ].map(a => (
              <div key={a.label} className="flex-1 rounded-md p-2 text-center" style={{
                backgroundColor: `${a.c}10`,
                border: `1px solid ${a.c}25`,
              }}>
                <p className="text-lg font-black" style={{ color: a.c, opacity: 0.7 }}>{a.count}</p>
                <p className="text-[8px] text-white/20">{a.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Runtime Bars */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Runtime Today</p>
        {[
          { m: 'CNC-04', run: 87, idle: 8, down: 5 },
          { m: 'Press-07', run: 92, idle: 5, down: 3 },
          { m: 'Weld-02', run: 45, idle: 15, down: 40 },
        ].map(m => (
          <div key={m.m} className="flex items-center gap-2 mb-1.5">
            <span className="text-[9px] text-white/25 font-mono w-16">{m.m}</span>
            <div className="flex-1 h-3 bg-white/[0.03] rounded-full overflow-hidden flex">
              <div style={{ width: `${m.run}%`, backgroundColor: GREEN, opacity: 0.4 }} />
              <div style={{ width: `${m.idle}%`, backgroundColor: AMBER, opacity: 0.4 }} />
              <div style={{ width: `${m.down}%`, backgroundColor: RED, opacity: 0.4 }} />
            </div>
          </div>
        ))}
      </div>
      {/* Alarms */}
      <div>
        {[
          { alarm: 'Weld-02: Temperature High', time: '2 min ago', c: RED },
          { alarm: 'CNC-04: Coolant Level Low', time: '18 min ago', c: AMBER },
          { alarm: 'Assy-01: Cycle Timeout', time: '1 hr ago', c: AMBER },
        ].map((a, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.03]">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-3 h-3" style={{ color: a.c }} />
              <span className="text-[11px] text-white/40">{a.alarm}</span>
            </div>
            <span className="text-[9px] text-white/20">{a.time}</span>
          </div>
        ))}
      </div>
    </MockupShell>
  );
}

/* ═══════════════════════════════════════════
   15. ENERGY MONITORING | Trend + Bars
   ═══════════════════════════════════════════ */
export function EnergyMockup() {
  return (
    <MockupShell title="Energy Monitor">
      {/* Trend Graph */}
      <div className="p-4 border-b border-white/[0.06]">
        <div className="flex justify-between mb-2">
          <span className="text-[10px] text-white/20 uppercase tracking-wider">Weekly Energy Trend (kWh)</span>
          <span className="text-[10px] font-bold" style={{ color: GREEN }}>-4.2% vs last week</span>
        </div>
        <svg viewBox="0 0 200 50" className="w-full h-14">
          {/* Grid lines */}
          {[10,20,30,40].map(y => (
            <line key={y} x1="0" y1={y} x2="200" y2={y} stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
          ))}
          {/* Energy line */}
          <polyline fill="none" stroke={BLUE_ACCENT} strokeWidth="1.5" opacity="0.6"
            points="10,35 40,32 70,38 100,30 130,28 160,25 190,22" />
          {/* Area fill */}
          <polygon fill={BLUE_ACCENT} opacity="0.06"
            points="10,35 40,32 70,38 100,30 130,28 160,25 190,22 190,48 10,48" />
          {[10,40,70,100,130,160,190].map((x,i) => (
            <circle key={i} cx={x} cy={[35,32,38,30,28,25,22][i]} r="1.5" fill={BLUE_ACCENT} opacity="0.5" />
          ))}
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d,i) => (
            <text key={d} x={10 + i * 30} y="48" textAnchor="middle" fill="rgba(255,255,255,0.12)" fontSize="5">{d}</text>
          ))}
        </svg>
      </div>
      {/* Machine-wise energy bars */}
      <div className="p-4 border-b border-white/[0.06]">
        <p className="text-[10px] text-white/20 uppercase tracking-wider mb-2">Machine-wise Consumption</p>
        {[
          { shop: 'CNC Shop', kwh: 847, pct: 70 },
          { shop: 'Press Shop', kwh: 1204, pct: 100 },
          { shop: 'Welding', kwh: 623, pct: 52 },
          { shop: 'Assembly', kwh: 412, pct: 34 },
        ].map(s => (
          <div key={s.shop} className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] text-white/30 w-20 truncate">{s.shop}</span>
            <div className="flex-1 h-3 bg-white/[0.03] rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{
                width: `${s.pct}%`,
                backgroundColor: s.pct > 80 ? AMBER : BLUE_ACCENT,
                opacity: 0.4,
              }} />
            </div>
            <span className="text-[10px] text-white/30 font-mono w-14 text-right">{s.kwh} kWh</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3">
        <StatBox label="Today" value="3,847 kWh" />
        <StatBox label="Cost/Part" value="Rs. 2.4" />
        <StatBox label="DG Usage" value="240L" />
      </div>
    </MockupShell>
  );
}
